import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import CompanyLogo from "@/components/company-logo";
import GlobalErrorPage from "@/components/global-error-page";
import {
  BOOT_GRACE_INTERVAL_MS,
  BOOT_MAX_GRACE_RETRIES,
} from "@/constants/boot";
import { Route } from "@/enums/route";
import { useBalances } from "@/hooks/useBalances";
import { useLogout } from "@/hooks/useLogout";
import { noteSilentSignal } from "@/lib/noteSilentSignal";
import { notifyBalancesStale } from "@/lib/notifyBalancesStale";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { resolveBootGate } from "@/lib/resolveBootGate";
import { resolveBootProgressKey } from "@/lib/resolveBootProgressKey";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { removeLoader } from "@/splashScreen";
import { runWithCleanup } from "@/utils/runWithCleanup";

// refetch() throws synchronously on a not-yet-started query (balances before
// /self yields a customerAccountId, or products/countries before their deferral
// timer fires). Wrap each so one un-started boot query can't abort the others.
// HTTP/network failures never reject — the refreshed query state drives render.
const safeRefetch = (run: () => unknown): Promise<unknown> => {
  try {
    return Promise.resolve(run());
  } catch {
    return Promise.resolve();
  }
};

interface SplashScreenProps {
  children: React.ReactNode;
  onSuccess?: (isSuccess: boolean) => void;
  isSwitchingAccount?: boolean;
  switchingAccountName?: string | null;
}

const SplashScreen = ({
  children,
  onSuccess,
  isSwitchingAccount = false,
  switchingAccountName = null,
}: SplashScreenProps) => {
  const tError = useTranslations("error");
  const tCommon = useTranslations("common");
  const tSplash = useTranslations("splash");
  const router = useRouter();
  const handleLogout = useLogout();

  const {
    isErrorProducts,
    isSuccessProducts,
    isErrorCountries,
    isSuccessCountries,
    refetchProducts,
    refetchCountries,
  } = useGlobalData();
  const {
    customerAccount,
    userProfile,
    isError: isUserError,
    isSuccess: isUserSuccess,
    refetchSelf,
  } = useCustomerAccount();

  const {
    balances,
    isError: isBalancesError,
    isLoading: isBalancesLoading,
    refetch: refetchBalances,
  } = useBalances();

  // Derive success from having balances and not being in error/loading state
  const isBalancesSuccess =
    !isBalancesLoading && !isBalancesError && Object.keys(balances).length > 0;

  const isSuccess =
    isUserSuccess &&
    isBalancesSuccess &&
    isSuccessProducts &&
    isSuccessCountries &&
    !!customerAccount;

  // Only show error if there's no data - allows polling failures to fail gracefully
  const hasBalancesData = Object.keys(balances).length > 0;

  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Balances counts as a boot failure only when there is no data to fall back to.
  const effectiveBalancesError = isBalancesError && !hasBalancesData;

  const bootVerdict = resolveBootGate({
    userError: isUserError,
    balancesError: effectiveBalancesError,
    productsError: isErrorProducts,
    countriesError: isErrorCountries,
    allSuccess: isSuccess,
    retryCount,
    maxRetries: BOOT_MAX_GRACE_RETRIES,
  });

  // Manual in-place boot retry: re-run every boot-critical fetch (rather than a
  // full page reload) and surface a progress hint while they settle. The retry
  // layer in baseQuery already auto-retries transient infra blips with backoff;
  // this is the user-facing escape hatch once those were exhausted.
  const handleBootRetry = useCallback(async () => {
    setIsRetrying(true);
    // runWithCleanup, not try/finally, keeps the React Compiler lint rules on
    // for this component.
    await runWithCleanup(
      () =>
        Promise.all([
          safeRefetch(refetchSelf),
          safeRefetch(refetchProducts),
          safeRefetch(refetchCountries),
          safeRefetch(refetchBalances),
        ]),
      () => setIsRetrying(false)
    );
  }, [refetchSelf, refetchProducts, refetchCountries, refetchBalances]);

  // Which boot resource the user is still waiting on — drives the retry hint.
  const bootProgressKey = resolveBootProgressKey({
    isAccountReady: isUserSuccess && !!userProfile,
    isProductsReady: isSuccessProducts,
    isCountriesReady: isSuccessCountries,
    isBalancesReady: isBalancesSuccess,
  });
  const bootProgressLabel =
    bootProgressKey === "account"
      ? tSplash("loadingAccount")
      : bootProgressKey === "products"
        ? tSplash("loadingProducts")
        : bootProgressKey === "countries"
          ? tSplash("loadingCountries")
          : bootProgressKey === "balances"
            ? tSplash("loadingBalances")
            : tSplash("finalizing");

  // Latest "refetch the failing boot resources" round, captured in a ref so the
  // timer effect depends only on primitives (render-stability.md Rule 6 — never
  // put RTK Query refetch identities in a dep array). Written after every
  // commit, never during render; the timer reads it when it fires, so it runs
  // the round of the latest committed render.
  const retryRoundRef = useRef<() => void>(() => {});
  useEffect(() => {
    retryRoundRef.current = () => {
      if (isUserError) void safeRefetch(refetchSelf);
      if (effectiveBalancesError) void safeRefetch(refetchBalances);
      if (isErrorProducts) void safeRefetch(refetchProducts);
      if (isErrorCountries) void safeRefetch(refetchCountries);
    };
  });

  // Patient boot grace: while a boot resource is errored but the grace budget
  // isn't spent, auto-refetch the failing resource(s) every BOOT_GRACE_INTERVAL_MS.
  // One timer per retryCount; recovery or exhaustion (bootVerdict leaving
  // "retrying") clears it. Layered above baseQuery's transient-infra retry so it
  // also covers the 401 token-refresh race that baseQuery deliberately won't retry.
  useEffect(() => {
    if (bootVerdict !== "retrying") return;
    const id = setTimeout(() => {
      retryRoundRef.current();
      setRetryCount((count) => count + 1);
    }, BOOT_GRACE_INTERVAL_MS);
    return () => clearTimeout(id);
    // render-stability.md Rule 6: refetch identities are captured via retryRoundRef;
    // omitting them from deps is intentional — only primitive signals drive the timer.
  }, [bootVerdict, retryCount]);

  useEffect(() => {
    if (bootVerdict === "ready" || bootVerdict === "error") {
      removeLoader();
      onSuccess?.(bootVerdict === "ready");
    }
  }, [bootVerdict, onSuccess]);

  // User-impact watchdogs (once per mount). The boot-failure event fires only
  // when grace is EXHAUSTED (bootVerdict === "error") — a recovered transient blip
  // produces no Sentry event (de-noises the issue, mirrors §11). The
  // grace-saved-a-boot case records a SIGNAL instead: the user saw nothing but a
  // slightly longer splash, so it is a metric to trend in Kibana, not an issue
  // for a human to open.
  const hasReportedBootFailure = useRef(false);
  const hasReportedRecovery = useRef(false);
  useEffect(() => {
    if (bootVerdict === "error" && !hasReportedBootFailure.current) {
      hasReportedBootFailure.current = true;
      reportSilentFailure({
        message: "[Boot] Boot-critical data failed — splash error page shown",
        fingerprint: "boot-data-error-page",
        extra: {
          userError: isUserError,
          balancesError: effectiveBalancesError,
          productsError: isErrorProducts,
          countriesError: isErrorCountries,
          retryCount,
        },
      });
    } else if (
      bootVerdict === "ready" &&
      retryCount > 0 &&
      !hasReportedRecovery.current
    ) {
      hasReportedRecovery.current = true;
      noteSilentSignal({
        message: "[Boot] Boot-critical data recovered after grace retries",
        signal: "boot-data-recovered",
        extra: { retryCount },
      });
    } else if (
      isUserSuccess &&
      !userProfile &&
      !hasReportedBootFailure.current
    ) {
      hasReportedBootFailure.current = true;
      reportSilentFailure({
        message: "[Boot] User profile missing after successful self load",
        fingerprint: "boot-missing-user-profile",
      });
    }
  }, [
    bootVerdict,
    retryCount,
    isUserSuccess,
    userProfile,
    isUserError,
    effectiveBalancesError,
    isErrorProducts,
    isErrorCountries,
  ]);

  // In-session balances refresh failure (data already present): keep the stale
  // balances, surface a throttled toast, let the next poll heal it. Distinct from
  // the boot grace, which handles the no-data cold-boot case.
  useEffect(() => {
    if (isBalancesError && hasBalancesData) {
      notifyBalancesStale(tError("balancesStale"));
    }
  }, [isBalancesError, hasBalancesData, tError]);

  // Manual retry OR auto-grace in progress — show the progress spinner instead of
  // the error page while the boot fetches re-run.
  if (isRetrying || bootVerdict === "retrying") {
    removeLoader();
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <div className="max-w-[300px]">
          <CompanyLogo
            id="globalLoaderSpinner"
            width={200}
            height={180}
            variant="default"
          />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          <span>{tSplash("retrying")}</span>
        </h2>
        <p className="text-base text-muted-foreground">
          <span>{bootProgressLabel}</span>
        </p>
      </div>
    );
  }

  if (bootVerdict === "error")
    return (
      <div className="flex items-center justify-center h-screen">
        <GlobalErrorPage
          title={tSplash("connectionTrouble")}
          subtitle={tSplash("retryHint")}
          cta={tSplash("retry")}
          onClick={() => {
            void handleBootRetry();
          }}
          onLogout={handleLogout}
        />
      </div>
    );

  if (!userProfile) {
    removeLoader();
    return (
      <>
        <GlobalErrorPage
          title={tError("noUserProfile")}
          subtitle={tError("pleaseTryAgainLater")}
          cta={tError("tryAgain")}
          onClick={() => router.push(Route.Dashboard)}
          onLogout={handleLogout}
        />
      </>
    );
  }

  // When switching accounts, keep content visible with loading overlay
  if (isSwitchingAccount) {
    removeLoader();
    return (
      <motion.div
        className="flex-1 relative"
        initial={false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 1 }}
      >
        <div className="absolute top-0 left-0 right-0 z-50">
          <div className="h-1 overflow-hidden">
            <motion.div
              className="h-full"
              style={
                {
                  background:
                    "linear-gradient(to right, var(--brand-primary), transparent)",
                } as React.CSSProperties
              }
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{
                duration: 0.45,
                ease: "easeOut",
              }}
            />
          </div>
        </div>
        {switchingAccountName && (
          <div className="absolute inset-0 z-40 flex items-start justify-center pt-20">
            <motion.div
              className="bg-white/90 backdrop-blur-sm px-6 py-4 rounded-lg shadow-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <p className="text-sm lg:text-lg font-semibold text-gray-900">
                {tCommon("switchingToAccount", {
                  accountName: switchingAccountName,
                })}
              </p>
            </motion.div>
          </div>
        )}
        <motion.div
          className="pointer-events-none"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </motion.div>
    );
  }

  // Assumed isSuccess - don't reset opacity on re-render to avoid flicker
  removeLoader();
  return (
    <motion.div
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex-1"
    >
      {children}
    </motion.div>
  );
};

export default SplashScreen;
